import { useEffect, useState, useRef } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  doc,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase/config';

const MAX_VISIBLE = 20;

export type NotificationType = 'mesa_paid' | 'transaction';

export interface NotificationDoc {
  id: string;
  type: NotificationType;
  mesaId: string;
  numeroMesa: string;
  total?: number;
  read: boolean;
  createdAt: Timestamp | null;
}

export function useNotifications(restaurantId: string | undefined) {
  const [list, setList] = useState<NotificationDoc[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!restaurantId) {
      setList([]);
      setUnreadCount(0);
      return () => {
        mountedRef.current = false;
      };
    }
    const col = collection(db, 'restaurants', restaurantId, 'notifications');
    const q = query(
      col,
      orderBy('createdAt', 'desc'),
      limit(MAX_VISIBLE)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        if (!mountedRef.current) return;
        const items: NotificationDoc[] = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            type: (data.type as NotificationType) || 'transaction',
            mesaId: data.mesaId ?? '',
            numeroMesa: data.numeroMesa ?? '',
            total: data.total,
            read: data.read === true,
            createdAt: data.createdAt ?? null,
          };
        });
        setList(items);
        setUnreadCount(items.filter((n) => !n.read).length);
      },
      (err) => {
        console.warn('useNotifications: snapshot error', err);
      }
    );
    return () => {
      mountedRef.current = false;
      // Defer unsubscribe to avoid Firestore "Unexpected state" during React unmount/Strict Mode
      const timeoutId = setTimeout(() => {
        try {
          unsub();
        } catch (_) {
          // ignore
        }
      }, 0);
      return () => clearTimeout(timeoutId);
    };
  }, [restaurantId]);

  const markAsRead = async (ids: string[]) => {
    if (!restaurantId || ids.length === 0) return;
    const batch = writeBatch(db);
    ids.forEach((id) => {
      const ref = doc(db, 'restaurants', restaurantId, 'notifications', id);
      batch.update(ref, { read: true });
    });
    await batch.commit();
  };

  const markAllVisibleAsRead = async () => {
    const unreadIds = list.filter((n) => !n.read).map((n) => n.id);
    await markAsRead(unreadIds);
  };

  return { list, unreadCount, markAsRead, markAllVisibleAsRead };
}
